import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Menu,
  Search,
  ShoppingBag,
  Ticket,
  Trophy,
  User,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import kourseLogo from '@/assets/kourse-logo.png';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';
import { useRunnerProfile } from '@/hooks/useRunnerProfile';
import { useShopEnabled } from '@/hooks/usePlatformSettings';
import { useAdminRole } from '@/hooks/useAdminRole';
import { Shield } from 'lucide-react';

type AccentKey = 'lime' | 'cyan' | 'pink' | 'yellow';

interface DropdownItem {
  label: string;
  description: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: AccentKey;
}

const EVENTS_ITEMS: DropdownItem[] = [
  { label: 'Courses officielles', description: '2 401 départs chronométrés. Route, trail, ultra.', to: '/explorer', icon: Trophy, accent: 'lime' },
  { label: 'Sorties communautaires', description: 'Footings, entraînements, runs entre potes — partout en France.', to: '/communaute', icon: Users, accent: 'cyan' },
  { label: 'Bourse au dossard', description: 'Revends ou rachète un dossard, en toute sécurité.', to: '/bourse-dossards', icon: Ticket, accent: 'pink' },
];

const ACCENT_CLASSES: Record<AccentKey, { tile: string; arrow: string }> = {
  lime:   { tile: 'group-hover/item:bg-[rgba(202,255,0,0.14)] group-hover/item:border-[rgba(202,255,0,0.4)] group-hover/item:text-primary',          arrow: 'group-hover/item:text-primary' },
  cyan:   { tile: 'group-hover/item:bg-[rgba(0,229,255,0.14)] group-hover/item:border-[rgba(0,229,255,0.4)] group-hover/item:text-accent',           arrow: 'group-hover/item:text-accent' },
  pink:   { tile: 'group-hover/item:bg-[rgba(255,45,158,0.14)] group-hover/item:border-[rgba(255,45,158,0.4)] group-hover/item:text-energy',         arrow: 'group-hover/item:text-energy' },
  yellow: { tile: 'group-hover/item:bg-[rgba(248,255,58,0.14)] group-hover/item:border-[rgba(248,255,58,0.4)] group-hover/item:text-[#f8ff3a]',      arrow: 'group-hover/item:text-[#f8ff3a]' },
};

function DropdownPanel({
  items,
  headerLabel,
  headerAccent,
  headerSub,
  footLabel,
  footTo,
  topBorderColors,
}: {
  items: DropdownItem[];
  headerLabel: string;
  headerAccent: string;
  headerSub: string;
  footLabel: string;
  footTo: string;
  topBorderColors: string;
}) {
  return (
    <div
      className="w-[420px] rounded-2xl border border-white/10 bg-[linear-gradient(180deg,#121218_0%,#0d0d12_100%)] p-3.5 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)] backdrop-blur-xl relative overflow-hidden"
    >
      <span aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{ background: topBorderColors }} />
      <div className="flex items-center justify-between px-2.5 pb-3 border-b border-white/5 mb-2 font-mono text-[10px] uppercase tracking-[0.12em]">
        <span style={{ color: headerAccent }}>{headerLabel}</span>
        <span className="text-muted-foreground/70">{headerSub}</span>
      </div>
      <div className="flex flex-col gap-0.5">
        {items.map((item) => {
          const accent = ACCENT_CLASSES[item.accent];
          return (
            <Link
              key={item.to + item.label}
              to={item.to}
              className="group/item flex items-start gap-3.5 p-3 rounded-[10px] hover:bg-white/[0.03] transition-colors"
            >
              <span
                className={cn(
                  'shrink-0 mt-0.5 grid place-items-center h-9 w-9 rounded-[9px] border border-white/[0.06] bg-white/[0.04] text-foreground transition-[background,border-color,color] duration-200',
                  accent.tile,
                )}
              >
                <item.icon className="h-4 w-4" />
              </span>
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-foreground">{item.label}</div>
                <div className="text-[11.5px] leading-snug text-muted-foreground/80 mt-0.5">{item.description}</div>
              </div>
              <span className={cn('self-center text-base text-muted-foreground/70 transition-[transform,color] duration-200 group-hover/item:translate-x-1', accent.arrow)}>
                →
              </span>
            </Link>
          );
        })}
      </div>
      <Link
        to={footTo}
        className="group/foot mt-2 flex items-center justify-between px-3.5 py-3 rounded-[10px] bg-[rgba(202,255,0,0.02)] text-[11px] uppercase tracking-[0.1em] font-semibold text-foreground hover:bg-[rgba(202,255,0,0.08)] hover:text-primary transition-colors border-t border-white/5"
      >
        <span>{footLabel}</span>
        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover/foot:translate-x-1" />
      </Link>
    </div>
  );
}

function NavItem({
  label,
  to,
  hasDropdown,
  children,
}: {
  label: string;
  to?: string;
  hasDropdown?: boolean;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpen(false), 180);
  };

  const handleEnter = () => {
    cancelClose();
    if (hasDropdown) setOpen(true);
  };

  useEffect(() => () => cancelClose(), []);

  // Fermeture clavier (Échap)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const inner = (
    <span
      className={cn(
        'relative inline-flex items-center gap-1.5 px-3.5 py-2.5 text-[13px] font-medium rounded-[10px] transition-colors duration-200',
        open
          ? 'text-foreground bg-white/[0.04]'
          : 'text-muted-foreground hover:text-foreground hover:bg-white/[0.04]',
      )}
    >
      {label}
      {hasDropdown && (
        <ChevronDown
          className={cn(
            'h-2.5 w-2.5 transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      )}
      <span
        className={cn(
          'absolute left-3.5 right-3.5 bottom-1.5 h-px bg-primary origin-left transition-transform duration-300 ease-[cubic-bezier(.2,.7,.2,1)]',
          open ? 'scale-x-100' : 'scale-x-0',
        )}
      />
    </span>
  );

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={scheduleClose}
      onFocus={handleEnter}
      onBlur={scheduleClose}
    >
      {to ? <Link to={to}>{inner}</Link> : <span className="cursor-pointer">{inner}</span>}
      {hasDropdown && (
        <div
          className={cn(
            'absolute top-[calc(100%+10px)] left-0 z-[200] transition-[opacity,transform] duration-200 ease-[cubic-bezier(.2,.7,.2,1)]',
            open
              ? 'opacity-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 -translate-y-1.5 pointer-events-none',
          )}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {children}
        </div>
      )}
    </div>
  );
}

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<'events' | 'shop' | null>(null);
  const { user, signOut } = useAuth();
  const { organization } = useOrganization();
  const { profile: runnerProfile } = useRunnerProfile();
  const { shopEnabled } = useShopEnabled();
  const { isAdmin, isLoading: adminLoading } = useAdminRole();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
    setMobileSection(null);
  }, [location.pathname]);

  const handleSignOut = async () => {
    setMobileOpen(false);
    await signOut();
    navigate('/');
  };

  const renderAuthButtons = (variant: 'desktop' | 'mobile') => {
    const mobile = variant === 'mobile';
    if (user) {
      // Tant que le rôle admin n'est pas chargé, ne pas afficher de bouton
      // dashboard pour éviter d'envoyer un admin vers un mauvais espace.
      if (adminLoading) {
        return null;
      }

      // Priorité absolue : un admin va toujours sur /admin, peu importe ce
      // que retourne useOrganization() (impersonation, cache, etc.).
      if (isAdmin) {
        return (
          <Link
            to="/admin"
            onClick={() => mobile && setMobileOpen(false)}
            className={cn(
              'inline-flex items-center gap-2 text-[13px] font-medium text-foreground rounded-[10px] transition-colors',
              mobile ? 'w-full justify-center px-4 py-3 border border-white/10' : 'px-3.5 py-2.5 hover:bg-white/5',
            )}
          >
            <Shield className="h-4 w-4" />
            Dashboard admin
          </Link>
        );
      }

      if (organization) {
        return (
          <Link
            to="/organisateur/tableau-de-bord"
            onClick={() => mobile && setMobileOpen(false)}
            className={cn(
              'inline-flex items-center gap-2 text-[13px] font-medium text-foreground rounded-[10px] transition-colors',
              mobile ? 'w-full justify-center px-4 py-3 border border-white/10' : 'px-3.5 py-2.5 hover:bg-white/5',
            )}
          >
            <LayoutDashboard className="h-4 w-4" />
            Tableau de bord
          </Link>
        );
      }
      // Tout utilisateur connecté (même sans profil coureur) accède à son espace coureur
      {
        return (
          <>
            {shopEnabled && (
              <Link
                to="/boutique"
                onClick={() => mobile && setMobileOpen(false)}
                className={cn(
                  'inline-flex items-center gap-2 text-[13px] font-medium text-foreground rounded-[10px] transition-colors',
                  mobile ? 'w-full justify-center px-4 py-3 border border-white/10' : 'px-3.5 py-2.5 hover:bg-white/5',
                )}
              >
                <ShoppingBag className="h-4 w-4" />
                Boutique
              </Link>
            )}
            <Link
              to="/coureur/tableau-de-bord"
              onClick={() => mobile && setMobileOpen(false)}
              className={cn(
                'inline-flex items-center gap-2 text-[13px] font-medium text-foreground rounded-[10px] transition-colors',
                mobile ? 'w-full justify-center px-4 py-3 border border-white/10' : 'px-3.5 py-2.5 hover:bg-white/5',
              )}
            >
              <User className="h-4 w-4" />
              Mon espace
            </Link>
          </>
        );
      }
      return (
        <Link
          to="/organisateur/inscription"
          onClick={() => mobile && setMobileOpen(false)}
          className={cn(
            'inline-flex items-center gap-2 text-[13px] font-medium text-foreground rounded-[10px] transition-colors',
            mobile ? 'w-full justify-center px-4 py-3 border border-white/10' : 'px-3.5 py-2.5 hover:bg-white/5',
          )}
        >
          <UserPlus className="h-4 w-4" />
          Devenir organisateur
        </Link>
      );
    }
    return null;
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-[padding,background,backdrop-filter,border-color] duration-300 border-b',
        scrolled
          ? 'py-2.5 bg-[rgba(10,10,12,0.78)] backdrop-blur-[22px] saturate-[1.4] border-white/[0.06]'
          : 'py-3.5 bg-transparent border-transparent',
      )}
    >
      <div className="mx-auto max-w-[1600px] px-5 lg:px-7 flex items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center shrink-0">
            <img src={kourseLogo} alt="Kourse" className="h-8 md:h-9 transition-transform group-hover:scale-105" width={174} height={40} />
          </Link>
          <span className="hidden lg:block h-[22px] w-px bg-gradient-to-b from-transparent via-white/15 to-transparent" />
          <nav className="hidden lg:flex items-center gap-1">
            <NavItem label="Événements" hasDropdown>
              <DropdownPanel
                items={EVENTS_ITEMS}
                headerLabel="// ÉVÉNEMENTS"
                headerAccent="hsl(var(--primary))"
                headerSub="3 entrées"
                footLabel="Calendrier complet"
                footTo="/calendrier"
                topBorderColors="linear-gradient(90deg, transparent, rgba(202,255,0,0.35), rgba(0,229,255,0.25), rgba(255,45,158,0.35), transparent)"
              />
            </NavItem>
            {shopEnabled && <NavItem label="Boutique" to="/boutique" />}
            <NavItem label="Organiser" to="/#organisateurs" />
            <NavItem label="À propos" to="/a-propos" />
          </nav>
        </div>

        <div className="flex items-center gap-2.5">
          <Link
            to="/explorer"
            aria-label="Rechercher"
            className="hidden md:grid h-[38px] w-[38px] place-items-center rounded-[10px] text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
          >
            <Search className="h-[15px] w-[15px]" />
          </Link>

          {user ? (
            <>
              <div className="hidden md:flex items-center gap-1">
                {renderAuthButtons('desktop')}
              </div>
              <button
                onClick={handleSignOut}
                className="hidden md:inline-flex items-center gap-1.5 text-[13px] font-medium text-foreground px-3.5 py-2.5 rounded-[10px] hover:bg-white/5 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Déconnexion
              </button>
            </>
          ) : (
            <>
              <Link
                to="/auth/connexion"
                className="hidden md:inline-flex text-[13px] font-medium text-foreground px-3.5 py-2.5 rounded-[10px] hover:bg-white/5 transition-colors"
              >
                Se connecter
              </Link>
              <Link
                to="/auth"
                className="hidden md:inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-[13px] font-bold text-primary-foreground whitespace-nowrap hover:shadow-[0_0_0_6px_rgba(202,255,0,0.18)] transition-shadow"
              >
                Créer un compte
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </>
          )}

          <button
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            className="lg:hidden grid h-10 w-10 place-items-center rounded-[10px] text-foreground hover:bg-white/5 transition-colors"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <div
        className={cn(
          'lg:hidden fixed left-0 right-0 top-0 bottom-0 z-[-1] bg-background transition-[opacity,visibility] duration-200',
          mobileOpen ? 'opacity-100 visible pointer-events-auto' : 'opacity-0 invisible pointer-events-none',
        )}
        style={{ paddingTop: scrolled ? 62 : 76 }}
      >
        <div className="h-full overflow-y-auto px-5 py-6">
          <div className="border-b border-white/5">
            <button
              onClick={() => setMobileSection((s) => (s === 'events' ? null : 'events'))}
              className="flex items-center justify-between w-full py-4 text-[16px] font-semibold text-foreground"
            >
              <span>Événements</span>
              <ChevronDown className={cn('h-4 w-4 transition-transform', mobileSection === 'events' && 'rotate-180')} />
            </button>
            {mobileSection === 'events' && (
              <div className="pb-3 flex flex-col gap-1">
                {EVENTS_ITEMS.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 py-2.5 px-2 rounded-lg"
                  >
                    <span className={cn('h-1.5 w-1.5 rounded-full', `bg-${item.accent === 'lime' ? 'primary' : item.accent === 'cyan' ? 'accent' : 'energy'}`)} />
                    <div>
                      <div className="text-[14px] font-semibold text-foreground">{item.label}</div>
                      <div className="text-[11.5px] text-muted-foreground">{item.description}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {shopEnabled && (
            <Link
              to="/boutique"
              onClick={() => setMobileOpen(false)}
              className="block py-4 text-[16px] font-semibold text-foreground border-b border-white/5"
            >
              Boutique
            </Link>
          )}

          {[
            { label: 'Organiser', to: '/#organisateurs' },
            { label: 'À propos', to: '/a-propos' },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className="block py-4 text-[16px] font-semibold text-foreground border-b border-white/5"
            >
              {item.label}
            </Link>
          ))}

          <div className="mt-6 space-y-3">
            {renderAuthButtons('mobile')}
            {user ? (
              <button
                onClick={handleSignOut}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 text-[13px] font-bold text-primary-foreground"
              >
                <LogOut className="h-4 w-4" />
                Déconnexion
              </button>
            ) : (
              <>
                <Link
                  to="/auth/connexion"
                  onClick={() => setMobileOpen(false)}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-[10px] border border-white/10 px-4 py-3 text-[13px] font-medium text-foreground"
                >
                  Se connecter
                </Link>
                <Link
                  to="/auth"
                  onClick={() => setMobileOpen(false)}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 text-[13px] font-bold text-primary-foreground"
                >
                  Créer un compte
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </>
            )}
          </div>

          <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground/70">
            // 2 401 COURSES · 13 RÉGIONS
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
